import { parseMarkdownFile } from './markdownUtils';
import { API_URL } from '../config/env';
import { createBearerJsonClient } from './apiFetchPolicy';

/**
 * Create a bearer-JSON client with authentication
 * @param {string} token - Authentication token
 * @returns {object} Configured client with get/post/put
 */
const createApiInstance = (token) => {
    return createBearerJsonClient(token, { baseURL: API_URL });
};

/**
 * Sync all items from ItemsContext to backend
 * @param {Array} allItems - Array of all items to sync
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} Result object with success status and message
 */
export const syncItemsToBackend = async (allItems, token) => {
    try {
        const api = createApiInstance(token);

        if (!allItems || allItems.length === 0) {
            throw new Error('No items found in ItemsContext to sync.');
        }

        // Map items to a backend-friendly shape (exclude React image objects)
        const itemsPayload = allItems.map((item) => {
            const payload = {
                id: item.id,
                title: item.title,
                shortTitle: item.shortTitle,
                service: item.service,
                description: item.description,
                price: item.price,
                category: item.category,
                details: item.details || {},
                detailSection: item.detailSection || item.details?.detailSection || null,
                contentPath: item.contentPath || null,
            };

            // Exclude image if it's a React require() object
            if (item.image && typeof item.image === 'string') {
                payload.image_url = item.image;
            }

            return payload;
        });

        const response = await api.post('/auth/items/bulk', { items: itemsPayload });

        if (response.status === 200 || response.status === 201) {
            return {
                success: true,
                message: 'Items synced to backend successfully.',
                count: itemsPayload.length
            };
        } else {
            throw new Error('Failed to sync items. Please check backend endpoint.');
        }
    } catch (error) {
        console.error('Error syncing items to backend:', error);
        return {
            success: false,
            message: error.message || 'Error syncing items. See console for details.',
            error: error
        };
    }
};

/**
 * Parse markdown file and save to backend for a specific item
 * @param {string} itemId - The item ID
 * @param {string} markdownContent - The markdown content to parse and save
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} Result object with success status, message, and parsed data
 */
export const parseAndSaveMarkdownToBackend = async (itemId, markdownContent, token) => {
    try {
        const api = createApiInstance(token);

        // Parse the markdown file
        const parsed = parseMarkdownFile(markdownContent);
        
        // Save to backend
        const response = await api.post(`/auth/items/${itemId}/content`, {
            content: parsed.body,
            frontMatter: parsed.frontMatter,
            fullContent: parsed.fullContent
        });

        if (response.status === 200 || response.status === 201) {
            return {
                success: true,
                message: 'Markdown content saved to backend successfully.',
                parsed: parsed
            };
        } else {
            throw new Error('Failed to save markdown content. Please check backend endpoint.');
        }
    } catch (error) {
        console.error('Error parsing and saving markdown to backend:', error);
        return {
            success: false,
            message: error.message || 'Error parsing and saving markdown. See console for details.',
            error: error
        };
    }
};
